import React, { Suspense, Fragment } from 'react';
import { EffectComposer, SMAA, SSAO, Bloom } from '@react-three/postprocessing';
import { BlendFunction, KernelSize } from 'postprocessing';

interface PostprocessingSettingsProps {
  postprocessingSettings:
    | {
        antialiasing?: string | null;
        bloom?: string | null;
        bloomExposure?: number | null;
        bloomRadius?: number | null;
        bloomStrength?: number | null;
        bloomThreshold?: number | null;
        ssao?: string | null;
        ssr?: string | null;
        ssrWidth?: number | null;
        ssrHeight?: number | null;
        ssrMaxDistance?: number | null;
        ssrOpacity?: number | null;
        id?: string;
        __typename?: string;
      }
    | null
    | undefined;
}

const PostprocessingSettings: React.FC<PostprocessingSettingsProps> = ({
  postprocessingSettings,
}) => {
  const {
    antialiasing,
    bloom,
    bloomExposure = 0.75,
    bloomRadius = 0,
    bloomStrength = 0.75,
    bloomThreshold = 0.3,
    ssao,
    ssr,
    ssrWidth = 1920,
    ssrHeight = 1080,
    ssrMaxDistance = 16,
    ssrOpacity = 0.1,
  } = postprocessingSettings || {};

  return (
    <Suspense fallback={null}>
      <EffectComposer enableNormalPass={ssao === 'on'}>
        <Fragment>
          {antialiasing === 'SMAA' && <SMAA />}

          {bloom === 'on' && (
            <Bloom
              intensity={bloomStrength ?? 0.75}
              luminanceThreshold={bloomThreshold ? bloomThreshold + 1 : 1}
              luminanceSmoothing={0.025}
              kernelSize={KernelSize.LARGE}
              radius={bloomRadius ?? 0}
              mipmapBlur={true}
            />
          )}

          {ssao === 'on' && (
            <SSAO
              blendFunction={BlendFunction.MULTIPLY}
              samples={30}
              rings={4}
              distanceThreshold={1.0}
              distanceFalloff={0.0}
              rangeThreshold={0.5}
              rangeFalloff={0.1}
              luminanceInfluence={0.9}
              radius={20}
              bias={0.5}
              worldDistanceThreshold={1.0}
              worldDistanceFalloff={0.0}
              worldProximityThreshold={1.0}
              worldProximityFalloff={0.0}
            />
          )}
        </Fragment>
      </EffectComposer>
    </Suspense>
  );
};

export default PostprocessingSettings;
